import React, { useMemo, useState } from 'react';
import { X, KeyRound, LayoutGrid } from 'lucide-react';
import AccessMatrix, { useAccessMatrix } from './AccessMatrix';
import { GridSection } from './permissionMap';

/**
 * Users > a person > a saved database role > the eye button: what they hold there and what they can do.
 *
 *   Keys held  every key as the resolver answers it for this database (GET /api/admin/resolve), with
 *              the rung that decided it — how they got it, or why not.
 *   Access     the access matrix with their answers: every page against every action, and for each
 *              line what they would need (a missing key, or the reason no grant fixes it).
 *
 * Advisory: nothing here grants. Grants and denies are made in the grids behind it.
 */
const SOURCE_LABEL = {
  UserOverride: 'Override', RoleDefault: 'Role Default', SysAdmin: 'SysAdmin',
  GlobalDeny: 'Global Deny', Global: 'Global', NoEntry: 'No Entry',
};
const SOURCE_CLASS = {
  GlobalDeny: 'stui-access-deny', Global: 'stui-access-grant', UserOverride: 'stui-access-warning', NoEntry: 'stui-text-muted',
};
const SOURCE_TITLE = {
  GlobalDeny: 'Denied globally — vetoed in every instance',
  Global: 'Granted globally — applies in every instance',
};
const TABS = [
  { id: 'keys', label: 'Keys held', icon: KeyRound },
  { id: 'access', label: 'Access', icon: LayoutGrid },
];

const UserAccessModal = ({ userId, userName, databaseId, databaseName, resolvedPerms, allPermissions, unsavedCount, onClose }) => {
  const { matrix, error } = useAccessMatrix(userId, databaseId, resolvedPerms);
  const [tab, setTab] = useState('keys');
  const [hideDenied, setHideDenied] = useState(false);
  const [onlyNo, setOnlyNo] = useState(false);

  const sources = useMemo(() => Object.fromEntries((resolvedPerms?.permissions || []).map(p => [p.permissionKey, p])), [resolvedPerms]);
  const defs = useMemo(() => Object.fromEntries(allPermissions.map(p => [p.permissionKey, p])), [allPermissions]);

  const all = useMemo(() => resolvedPerms?.permissions || [], [resolvedPerms]);
  const held = useMemo(() => all
    .filter(p => !hideDenied || p.access !== 'Deny')
    .slice()
    .sort((a, b) => (defs[a.permissionKey]?.category || '').localeCompare(defs[b.permissionKey]?.category || '')
      || (defs[a.permissionKey]?.displayName || a.permissionKey).localeCompare(defs[b.permissionKey]?.displayName || b.permissionKey)),
  [all, hideDenied, defs]);

  const globalCount = (resolvedPerms?.globalPermissions || []).length;
  const scope = matrix?.isSysAdmin ? ' — SysAdmin: everything' : matrix && !matrix.member ? ' — no role here: only global grants count' : '';

  return (
    <div className="stui-modal-overlay">
      <div className="stui-modal stui-modal-xxl">
        <div className="stui-modal-header">
          <h3>Effective Permissions — {userName} / {databaseName}</h3>
          <button className="stui-modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="stui-modal-body">
          <div className="stui-module-header-tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} className={`stui-dataentry-tab${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          {unsavedCount > 0 && (
            <div className="stui-inline-notice stui-mt-md">
              Showing saved permissions — {unsavedCount} unsaved change{unsavedCount === 1 ? '' : 's'} not reflected until you Save.
            </div>
          )}

          {tab === 'keys' && (
            <GridSection height="62vh"
              title={`Role ${resolvedPerms?.resolvedRole || ''}${globalCount > 0 ? ` + ${globalCount} global` : ''} — showing ${held.length} of ${all.length}`}
              columns={[{ label: 'Category', width: '16%' }, { label: 'Permission', width: '26%' }, { label: 'Key' }, { label: 'Access', width: '9%' }, { label: 'Source', width: '13%' }]}
              rows={held.map(p => ({
                key: p.permissionKey,
                cells: [
                  defs[p.permissionKey]?.category || '',
                  { value: defs[p.permissionKey]?.displayName || p.permissionKey, className: 'stui-fw-semibold' },
                  { value: <code>{p.permissionKey}</code>, className: 'stui-truncate', title: p.permissionKey },
                  { value: p.access, className: `stui-fw-semibold ${p.access === 'Grant' ? 'stui-access-grant' : 'stui-access-deny'}` },
                  { value: SOURCE_LABEL[p.source] || 'No Entry', className: SOURCE_CLASS[p.source], title: SOURCE_TITLE[p.source] },
                ],
              }))} />
          )}

          {tab === 'access' && (
            <>
              {error && <div className="stui-alert stui-alert-error stui-mt-md">{error}</div>}
              {!matrix && !error && <p className="stui-hint stui-mt-md">Working out what they can do on {databaseName}...</p>}
              {matrix && (
                <AccessMatrix matrix={matrix} sources={sources} height="62vh" onlyNo={onlyNo}
                  title={`Every page (${matrix.lines.length} lines; double-click for the gates)${scope}`} />
              )}
            </>
          )}
        </div>
        <div className="stui-modal-footer stui-modal-footer-split">
          {tab === 'keys' ? (
            <label className="stui-checkbox">
              <input type="checkbox" className="stui-checkbox-input" checked={hideDenied} onChange={e => setHideDenied(e.target.checked)} />
              Hide denied
            </label>
          ) : (
            <label className="stui-checkbox">
              <input type="checkbox" className="stui-checkbox-input" checked={onlyNo} onChange={e => setOnlyNo(e.target.checked)} />
              Only what they cannot do
            </label>
          )}
          <button className="stui-btn stui-btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default UserAccessModal;
